import React from "react";
import Autocomplete, { AutocompleteProps } from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import LoadingSkeleton from "@app.automotus.io/components/LoadingSkeleton";
import { useQuery } from "@apollo/client";
import { GET_PERMIT_AUTHORITIES, GetPermitAuthoritiesData, PermitAuthorityRaw } from "common/graphql/queries";

export interface PermitAuthority {
  id: string;
  name: string;
}

export interface PermitAuthorityAutocompleteProps
  extends Omit<
    AutocompleteProps<PermitAuthority, false, boolean | undefined, false>,
    "options" | "renderInput" | "getOptionLabel" | "isOptionEqualToValue" | "onChange"
  > {
  /** Currently selected authority */
  value: PermitAuthority | null;
  /** Called when selection changes */
  onChange: (authority: PermitAuthority | null) => void;
  /** Label for the input field */
  label?: string;
  /** Placeholder text */
  placeholder?: string;
  /** Whether the field is required */
  required?: boolean;
  /** Error state */
  error?: boolean;
  /** Helper text to display below the field */
  helperText?: string;
}

const mapRawToPermitAuthority = (authority: PermitAuthorityRaw): PermitAuthority => ({
  id: authority.payeeAccountId,
  name: authority.name,
});

export const PermitAuthorityAutocomplete: React.FC<PermitAuthorityAutocompleteProps> = ({
  value,
  onChange,
  label = "Permitting Authority",
  placeholder = "Select a city...",
  required = true,
  helperText,
  disabled,
  ...autocompleteProps
}) => {
  const { data, loading, error } = useQuery<GetPermitAuthoritiesData>(GET_PERMIT_AUTHORITIES);

  const authorities: PermitAuthority[] = data?.authorities.map(mapRawToPermitAuthority) ?? [];

  if (error) {
    return (
      <TextField fullWidth label={label} disabled error helperText="Failed to load authorities. Please try again." />
    );
  }

  return (
    <LoadingSkeleton loading={loading} width="100%">
      <Autocomplete
        {...autocompleteProps}
        value={value}
        onChange={(_, newValue) => onChange(newValue)}
        options={authorities}
        getOptionLabel={(option) => option.name}
        isOptionEqualToValue={(option, val) => option.id === val.id}
        disabled={disabled || loading}
        fullWidth
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            placeholder={placeholder}
            required={required}
            error={error}
            helperText={helperText}
          />
        )}
      />
    </LoadingSkeleton>
  );
};

export default PermitAuthorityAutocomplete;
